import { Component, OnDestroy, OnInit } from '@angular/core';
import { UIRouter } from '@uirouter/angular';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { ActionViewCategoryTypes, ActionViewDisplayTypes } from '../../constants.new';
import { SearchPageType, UserSaveService } from '../../Navigation/User-Save/userSave.service';
import { SmartSearchService } from '../../Search/Advanced/smartSearch.service';
import { DraggableOutputColumn } from '../../Common/AV-AS/category.column.picker.component';
import { TaskSeriesType } from '../Task.Model';
import { UserInstanceService } from '../../User/userInstance.service';
import { ActionViewOutputCustomComponent } from './Action-View-Output-Custom/actionViewOutputCustom.component';
import { ActionViewPersistenceService } from './Action.View.Persistence.Service.upgrade';
import {
    ActionViewFilterColumn,
    ACTION_VIEW_SEARCH_TIMEOUT_MESSAGE,
    ACTION_VIEW_SEARCH_TIMEOUT_TITLE,
    getActionViewFieldId,
    isActionViewSearchTimeout,
    normalizeActionViewRequest,
    parseActionViewSavedCriteria,
    serializeActionViewSavedCriteria,
    reuseActionViewFilterValues,
    toActionViewOutputColumn,
} from './actionView.adapters';
import { ActionViewCriteriaData, ActionViewCriteriaService } from './actionViewCriteria.service';
import { ActionViewApiService } from './actionView.service';
import { ActionViewPlainEnglishService } from './actionViewPlainEnglish.service';
import { ActionViewStateService } from './actionViewState.service';
import {
    ActionViewDefinition,
    ActionViewDisplay,
    ActionViewOverviewDataItem,
    ActionViewOverviewResult,
    ActionViewSavedSearch,
    ActionViewSearchRequest,
    ActionViewSearchResult,
    ActionViewTaskType,
    ActionViewUiColumn,
    TaskActionViewCompletedOption,
    TaskActionViewDateTimeOption,
    TaskActionViewIncludeOption,
    TaskActionViewOrganizeBy,
    TaskActionViewShowOption,
    TaskActionViewThatAreOption,
    TaskActionViewWhereOption,
} from './actionView.models';
import { ActionViewSaveComponent, ActionViewSaveResult } from './Save/actionViewSave.component';

interface InvalidActionViewColumn {
    colName: string;
    offendingColumns: string[];
}

@Component({
    selector: 'action-view-page',
    templateUrl: './actionView.component.html',
    styleUrls: ['./actionView.component.scss'],
    standalone: false,
})
export class ActionViewComponent implements OnInit, OnDestroy {
    constructor(
        private readonly router: UIRouter,
        private readonly actionViewService: ActionViewApiService,
        private readonly actionViewPersistenceService: ActionViewPersistenceService,
        private readonly criteriaService: ActionViewCriteriaService,
        private readonly stateService: ActionViewStateService,
        private readonly smartSearchService: SmartSearchService,
        private readonly plainEnglishService: ActionViewPlainEnglishService,
        private readonly modalService: WeissmanModalService,
        private readonly restrictService: RestrictService,
        private readonly userInstanceService: UserInstanceService,
        private readonly userSaveService: UserSaveService,
        private readonly toastr: ToastrService,
    ) {}

    criteria: ActionViewSearchRequest = this.newCriteria();
    filterColumns: ActionViewUiColumn[] = [];
    taskTypes: ActionViewTaskType[] = [];
    overviewResults: ActionViewOverviewResult[] = [];
    detailedResults: ActionViewSearchResult | null = null;
    definition: ActionViewDefinition = this.emptyDefinition();
    display: ActionViewDisplay = 'filter';
    invalidColumns: InvalidActionViewColumn[] = [];
    dataLoaded: boolean = false;
    loading: boolean = false;
    showOrs: boolean = false;
    isOverviewTimedOut: boolean = false;
    drilldownMode: boolean = false;
    drilldownCriteria: ActionViewSearchRequest | null = null;
    criteriaData: ActionViewCriteriaData | null = null;

    readonly completed = TaskActionViewThatAreOption.Completed;
    readonly urgencies = this.actionViewService.getUrgencies();
    readonly hasEditPermission = this.restrictService.isInRole(Roles.SYSTEMSEARCHESEDIT);
    readonly singleInstanceSelected = this.userInstanceService.isSingleInstanceSelected();

    private savedActionView: ActionViewSavedSearch | null = null;

    get title(): string {
        return this.definition.actionViewName || 'Action View';
    }

    get isSystemView(): boolean {
        return this.definition.actionViewType === ActionViewCategoryTypes.SYSTEM;
    }

    get canSave(): boolean {
        if (!this.definition.actionViewId) {
            return false;
        }
        return this.isSystemView ? this.hasEditPermission : this.definition.isOwnedByUser;
    }

    get canRevokeShare(): boolean {
        return !this.isSystemView && this.definition.isOwnedByUser && this.definition.isShared;
    }

    get plainEnglish(): string {
        const filter = this.drilldownMode && this.drilldownCriteria ? this.drilldownCriteria : this.criteria;
        return this.plainEnglishService.getForFilter(
            this.drilldownMode ? filter : { ...filter, filters: [] },
            this.filterColumns,
        );
    }

    async ngOnInit(): Promise<void> {
        try {
            this.loading = true;
            this.criteriaData = await this.criteriaService.load();
            this.taskTypes = this.criteriaData.taskTypes;
            await this.initializeSession();
            this.dataLoaded = true;
        } catch (error) {
            this.toastr.error('', 'Unable to load Action View');
            console.error('Unable to initialize Angular Action View', error);
        } finally {
            this.loading = false;
        }
    }

    ngOnDestroy(): void {
        this.actionViewService.cancelSearch();
    }

    updateCriteria(criteria: ActionViewSearchRequest): void {
        this.criteria = criteria;
        this.stateService.updateMasterCriteria(criteria, this.showOrs);
    }

    updateFilterColumns(columns: ActionViewUiColumn[]): void {
        this.filterColumns = columns;
        this.stateService.updateFilterColumns(columns);
        this.updateInvalidColumns();
    }

    updateShowOrs(showOrs: boolean): void {
        this.showOrs = showOrs;
        this.criteria = { ...this.criteria, showOrs };
        this.stateService.updateMasterCriteria(this.criteria, showOrs);
    }

    taskTypeChanged(taskType: ActionViewTaskType): void {
        if (!this.criteriaData) {
            return;
        }
        const output = this.criteriaService.getOutputSelection(
            this.criteriaData,
            taskType.taskSeriesTypeID,
            taskType.taskTypeID,
            this.filterColumns,
        );
        this.filterColumns = output.filterColumns;
        this.stateService.updateFilterColumns(this.filterColumns);
        this.updateCriteria({
            ...this.criteria,
            taskSeriesTypeID: taskType.taskSeriesTypeID,
            taskTypeID: taskType.taskTypeID,
            taskOutputColumns: output.taskOutputColumns,
            entityOutputColumns: output.entityOutputColumns,
            customOutput: output.customOutput,
            output: 'default',
            taskTypeName: output.taskTypeName,
            persistCustomColumns: false,
        });

        this.updateInvalidColumns();
    }

    async launchCustomOutput(): Promise<void> {
        if (!this.criteriaData) {
            return;
        }
        const taskType =
            this.taskTypes.find(
                (item) =>
                    item.taskSeriesTypeID === this.criteria.taskSeriesTypeID &&
                    item.taskTypeID === this.criteria.taskTypeID,
            ) ?? this.taskTypes[0];
        const currentColumns = this.criteriaService.resolveOutputColumns(this.criteriaData, [
            ...this.criteria.taskOutputColumns,
            ...this.criteria.entityOutputColumns,
        ]);
        const columns = await this.modalService.showAsync(
            ActionViewOutputCustomComponent,
            {
                fromActionView: true,
                taskType: taskType as unknown as TaskSeriesType,
                systemColumns: currentColumns as unknown as DraggableOutputColumn[],
                userColumns: currentColumns as unknown as DraggableOutputColumn[],
            },
            'modal-lg',
        );
        if (!columns) {
            return;
        }
        const uiColumns: ActionViewUiColumn[] = columns.map((column) => ({
            ...column,
            operators: (column.operators ?? []).map((operator) =>
                typeof operator === 'string' ? { name: operator } : { ...operator, name: operator.name ?? '' },
            ),
            or: (column.or ?? []).map((element) => ({
                value: element.value,
                operator: element.operator,
            })),
        }));
        const normalized = uiColumns.map(toActionViewOutputColumn);
        const filterableColumns = uiColumns.filter((column) => {
            const fieldId = getActionViewFieldId(column);
            return !(fieldId > 9999 && fieldId < 11000) && ![9002, 1430].includes(fieldId);
        });
        this.filterColumns = reuseActionViewFilterValues(filterableColumns, this.filterColumns);
        this.stateService.updateFilterColumns(this.filterColumns);
        this.updateCriteria({
            ...this.criteria,
            output: 'custom',
            customOutput: true,
            taskOutputColumns: normalized.filter((column) => column.id > 9999 && column.id < 11000),
            entityOutputColumns: normalized.filter((column) => !(column.id > 9999 && column.id < 11000)),
            persistCustomColumns: this.criteria.persistCustomColumns ?? true,
        });
        this.updateInvalidColumns();
    }

    chooseDefaultOutput(): void {
        const selected = this.taskTypes.find(
            (taskType) =>
                taskType.taskSeriesTypeID === this.criteria.taskSeriesTypeID &&
                taskType.taskTypeID === this.criteria.taskTypeID,
        );
        if (selected) {
            this.taskTypeChanged(selected);
        }
    }

    chooseCustomOutput(): void {
        this.updateCriteria({ ...this.criteria, output: 'custom', persistCustomColumns: true });
    }

    updatePersistCustomColumns(persistCustomColumns: boolean): void {
        this.updateCriteria({ ...this.criteria, persistCustomColumns });
    }

    async showOverview(): Promise<void> {
        if (!this.validate()) {
            return;
        }
        const criteria = this.executionCriteria(false);
        this.criteria = criteria;
        this.display = 'overview';
        this.drilldownMode = false;
        this.drilldownCriteria = null;
        this.isOverviewTimedOut = false;
        this.stateService.updateMasterCriteria(criteria, this.showOrs);
        this.stateService.setDisplay('overview');
        this.stateService.startOverviewSearch();
        this.loading = true;
        try {
            const results = await this.actionViewService.executeActionView(criteria, this.definition.actionViewId);
            if (!results) {
                return;
            }
            console.info(['ActionView Overview Search results:', results]);
            console.info(['ActionView Overview Search result sql:', results.sql]);
            this.overviewResults = results.group1ResultList ?? [];
            this.stateService.storeOverview({
                results: this.overviewResults,
                headers: this.overviewResults[0]?.data.map((item) => item.label) ?? [],
                organizeTotals: [],
                grandTotals: { value: 0, ready: 0, notReady: 0 },
                groupByCount: Math.min(criteria.groupBy.length, 2) as 0 | 1 | 2,
            });
        } catch (error) {
            if (isActionViewSearchTimeout(error)) {
                this.isOverviewTimedOut = true;
                this.overviewResults = [];
                this.stateService.markOverviewTimedOut();
                this.toastr.error(ACTION_VIEW_SEARCH_TIMEOUT_MESSAGE, ACTION_VIEW_SEARCH_TIMEOUT_TITLE);
            } else {
                this.toastr.error('', 'Action View search failed');
            }
            console.error('Angular Action View overview search failed', error);
        } finally {
            this.loading = false;
        }
    }

    showDetailed(): void {
        if (!this.validate()) {
            return;
        }
        this.detailedResults = null;
        this.drilldownMode = false;
        this.drilldownCriteria = null;
        this.clearLegacyDetailedState();
        this.criteria = this.executionCriteria(true);
        this.display = 'detailed';
        this.stateService.updateMasterCriteria(this.criteria, this.showOrs);
        this.stateService.setDisplay('detailed');
    }

    drilldown(criteria: ActionViewSearchRequest): void {
        if (!this.criteriaData) {
            return;
        }
        this.detailedResults = null;
        this.clearLegacyDetailedState();
        let drilldown = criteria;
        if (!criteria.persistCustomColumns) {
            const output = this.criteriaService.getOutputSelection(
                this.criteriaData,
                criteria.taskSeriesTypeID,
                criteria.taskTypeID,
            );
            drilldown = {
                ...drilldown,
                taskOutputColumns: output.taskOutputColumns,
                entityOutputColumns: output.entityOutputColumns,
                customOutput: output.customOutput,
            };
        }
        this.drilldownCriteria = drilldown;
        this.drilldownMode = true;
        this.display = 'detailed';
        this.stateService.beginDrilldown(drilldown);
    }

    backToOverview(shouldCancel: boolean): void {
        if (shouldCancel) {
            this.actionViewService.cancelSearch();
        }
        this.detailedResults = null;
        this.drilldownCriteria = null;
        this.drilldownMode = false;
        this.clearLegacyDetailedState();
        this.display = 'overview';
        this.stateService.restoreOverview();
    }

    showFilter(): void {
        this.actionViewService.cancelSearch();
        this.display = 'filter';
        this.drilldownMode = false;
        this.drilldownCriteria = null;
        this.stateService.setDisplay('filter');
    }

    async saveAs(): Promise<void> {
        const result = await this.modalService.showAsync(
            ActionViewSaveComponent,
            {
                definition: this.definition,
                criteria: this.actionViewService.toSavedCriteria(this.criteria, this.filterColumns),
                hasEditPermission: this.hasEditPermission,
            },
            'modal-lg',
        );
        if (!result) {
            return;
        }
        this.userSaveService.setDirty(SearchPageType.AV);
        await this.navigateToSaved(result);
    }

    async save(): Promise<void> {
        if (!this.savedActionView || !this.canSave) {
            return;
        }
        const updated: ActionViewSavedSearch = {
            ...this.savedActionView,
            actionViewName: this.definition.actionViewName,
            criteria: serializeActionViewSavedCriteria(
                this.actionViewService.toSavedCriteria(this.criteria, this.filterColumns),
            ),
            defaultDisplay: this.definition.defaultDisplay,
            rowVersion: this.definition.rowVersion,
        };
        this.savedActionView = await this.actionViewService.updateActionView(
            updated,
            this.definition.actionViewType as ActionViewCategoryTypes,
        );
        this.definition.rowVersion = this.savedActionView.rowVersion;
        this.userSaveService.setDirty(SearchPageType.AV);
        this.toastr.success('Action View saved');
    }

    async toggleFavorite(favorite: boolean): Promise<void> {
        if (!this.definition.actionViewId) {
            return;
        }
        await this.actionViewService.toggleFavorite(this.definition.actionViewId, favorite);
        this.definition = { ...this.definition, isFavorite: favorite };
        this.userSaveService.setDirty(SearchPageType.AV);
    }

    async revokeShare(): Promise<void> {
        if (!this.savedActionView) {
            return;
        }
        await this.actionViewService.revokeShare({
            actionViewID: this.savedActionView.actionViewId,
            isShared: false,
            rowVersion: this.savedActionView.rowVersion,
        });
        this.definition = { ...this.definition, isShared: false };
        this.userSaveService.setDirty(SearchPageType.AV);
    }

    async reload(): Promise<void> {
        this.stateService.clear();
        await this.initializeSession();
    }

    dismissInvalidColumns(): void {
        this.invalidColumns = [];
    }

    private async initializeSession(): Promise<void> {
        if (!this.criteriaData) {
            return;
        }
        const params = this.router.globals.params;
        const actionViewId = Number(params.actionViewId) || null;
        const actionViewType =
            params.actionViewType === undefined || params.actionViewType === null
                ? ActionViewCategoryTypes.CUSTOM
                : (Number(params.actionViewType) as ActionViewCategoryTypes);
        const persistedCriteria = actionViewId ? null : this.getPersistedMasterCriteria();
        const defaultDisplayType = persistedCriteria ? ActionViewDisplayTypes.OVERVIEW : ActionViewDisplayTypes.FILTER;
        const displayType = Number(params.displayType) || defaultDisplayType;
        const cached = this.stateService.snapshot;

        if (cached?.route.state === 'actionview' && cached.definition.actionViewId === actionViewId) {
            this.definition = structuredClone(cached.definition);
            this.criteria = structuredClone(cached.masterCriteria);
            this.display = cached.display;
            this.drilldownCriteria = structuredClone(cached.drilldownCriteria);
            this.drilldownMode = cached.drilldownMode;
            this.overviewResults = structuredClone(cached.overview?.results ?? []);
            this.isOverviewTimedOut = cached.isOverviewTimedOut;
            this.detailedResults = structuredClone(cached.detail?.results ?? null);
            this.showOrs = cached.showOrs;
            this.filterColumns = structuredClone(cached.filterColumns);
            return;
        }

        if (actionViewId) {
            this.savedActionView =
                actionViewType === ActionViewCategoryTypes.SYSTEM
                    ? await this.actionViewService.getSystemActionViewById(actionViewId)
                    : await this.actionViewService.getCustomActionViewById(actionViewId);
            const savedCriteria = parseActionViewSavedCriteria(this.savedActionView.criteria);
            const hydratedCriteria = this.criteriaService.hydrateSavedOutputColumns(
                this.criteriaData,
                savedCriteria.masterFilter,
            );
            this.criteria = this.criteriaService.ensureAllowedShowChoice(hydratedCriteria, this.criteriaData);
            this.filterColumns = this.criteriaService.resolveSavedFilterColumns(
                this.criteriaData,
                savedCriteria.filters,
                savedCriteria.outputColumns,
            );
            this.definition = {
                actionViewId: this.savedActionView.actionViewId,
                actionViewName: this.savedActionView.actionViewName,
                actionViewType,
                categoryId: this.savedActionView.categoryId,
                defaultDisplay: this.savedActionView.defaultDisplay,
                isFavorite: this.savedActionView.isFavorite ?? false,
                isShared: this.savedActionView.isShared,
                isOwnedByUser: this.savedActionView.isOwnedByUser,
                rowVersion: this.savedActionView.rowVersion,
            };
            this.showOrs = Boolean(this.criteria.showOrs);
        } else if (persistedCriteria) {
            this.savedActionView = null;
            this.definition = this.emptyDefinition();
            this.criteria = this.criteriaService.ensureAllowedShowChoice(
                this.withDefaultOutput(persistedCriteria),
                this.criteriaData,
            );
            this.filterColumns = this.resolvePersistedFilterColumns(this.criteria);
            this.showOrs = Boolean(this.actionViewPersistenceService.showOrs ?? this.criteria.showOrs);
        } else {
            this.savedActionView = null;
            this.definition = this.emptyDefinition();
            this.criteria = this.criteriaService.ensureAllowedShowChoice(this.newCriteria(), this.criteriaData);
            const firstTaskType = this.taskTypes[0];
            if (firstTaskType) {
                const output = this.criteriaService.getOutputSelection(
                    this.criteriaData,
                    firstTaskType.taskSeriesTypeID,
                    firstTaskType.taskTypeID,
                );
                this.criteria = {
                    ...this.criteria,
                    taskSeriesTypeID: firstTaskType.taskSeriesTypeID,
                    taskTypeID: firstTaskType.taskTypeID,
                    taskTypeName: output.taskTypeName,
                    taskOutputColumns: output.taskOutputColumns,
                    entityOutputColumns: output.entityOutputColumns,
                    customOutput: output.customOutput,
                };
                this.filterColumns = output.filterColumns;
            }
        }

        this.display = this.toDisplay(displayType, this.criteria.thatAreChoice);
        this.stateService.startSession(
            {
                state: 'actionview',
                params: { actionViewId: actionViewId ?? undefined, actionViewType, displayType },
            },
            this.definition,
            this.criteria,
            this.display,
            this.filterColumns,
        );
        this.updateInvalidColumns();

        if (this.actionViewPersistenceService.externalDrilldown) {
            await this.showOverview();
            this.applyExternalDrilldown();
            this.actionViewPersistenceService.externalDrilldown = false;
            this.actionViewPersistenceService.externalDrilldownData = null;
        } else if (this.display === 'overview') {
            await this.showOverview();
        } else if (this.display === 'detailed') {
            this.criteria = this.executionCriteria(true);
            this.stateService.updateMasterCriteria(this.criteria, this.showOrs);
        }
    }

    private getPersistedMasterCriteria(): ActionViewSearchRequest | null {
        const masterFilter = this.actionViewPersistenceService.masterFilter;
        if (!masterFilter || typeof masterFilter !== 'object' || Array.isArray(masterFilter)) {
            return null;
        }
        const persistedFilter = masterFilter as Record<string, unknown>;
        if (Object.keys(persistedFilter).length === 0) {
            return null;
        }
        return normalizeActionViewRequest(persistedFilter);
    }

    private clearLegacyDetailedState(): void {
        this.actionViewPersistenceService.detailResults = null;
        this.actionViewPersistenceService.drilldownFilter = null;
        this.actionViewPersistenceService.drilldownMode = false;
        this.actionViewPersistenceService.filter = null;
        this.actionViewPersistenceService.sort = null;
    }

    private withDefaultOutput(criteria: ActionViewSearchRequest): ActionViewSearchRequest {
        if (criteria.taskOutputColumns.length || criteria.entityOutputColumns.length || !this.criteriaData) {
            return criteria;
        }
        const output = this.criteriaService.getOutputSelection(
            this.criteriaData,
            criteria.taskSeriesTypeID,
            criteria.taskTypeID,
        );
        return {
            ...criteria,
            taskOutputColumns: output.taskOutputColumns,
            entityOutputColumns: output.entityOutputColumns,
            customOutput: output.customOutput,
        };
    }

    private resolvePersistedFilterColumns(criteria: ActionViewSearchRequest): ActionViewUiColumn[] {
        const persistedFilterColumns = this.actionViewPersistenceService.filterColumns as ActionViewUiColumn[] | null;
        if (Array.isArray(persistedFilterColumns) && persistedFilterColumns.length) {
            return structuredClone(persistedFilterColumns);
        }
        if (!this.criteriaData) {
            return [];
        }
        const outputColumnIds = [...criteria.taskOutputColumns, ...criteria.entityOutputColumns].map((column) =>
            getActionViewFieldId(column),
        );
        const resolved = this.criteriaService.resolveSavedFilterColumns(
            this.criteriaData,
            criteria.filters,
            outputColumnIds,
        );
        if (resolved.length) {
            return resolved;
        }
        return this.criteriaService.getOutputSelection(
            this.criteriaData,
            criteria.taskSeriesTypeID,
            criteria.taskTypeID,
        ).filterColumns;
    }

    private applyExternalDrilldown(): void {
        const externalDrilldownData = this.actionViewPersistenceService.externalDrilldownData as {
            task?: Partial<ActionViewOverviewResult>;
            item?: Partial<ActionViewOverviewDataItem>;
            ready?: unknown;
        } | null;
        if (!externalDrilldownData?.task || !externalDrilldownData.item?.label) {
            return;
        }
        let drilldown = {
            ...structuredClone(this.criteria),
            taskSeriesTypeID: Number(externalDrilldownData.task.taskSeriesTypeID) || this.criteria.taskSeriesTypeID,
            taskTypeID: Number(externalDrilldownData.task.taskTypeID) || this.criteria.taskTypeID,
        };
        drilldown = this.withExternalOrganizeFilter(drilldown, externalDrilldownData.item.label);
        drilldown = this.withExternalReadiness(drilldown, externalDrilldownData.ready);
        this.drilldown({ ...drilldown, detailedResults: true });
    }

    private withExternalOrganizeFilter(request: ActionViewSearchRequest, header: string): ActionViewSearchRequest {
        switch (request.organizeBy) {
            case TaskActionViewOrganizeBy.Urgency:
                return {
                    ...request,
                    useUrgencyFilter: true,
                    urgencyChoice: this.actionViewService.getUrgencyChoiceByHeaderName(header),
                };
            case TaskActionViewOrganizeBy.DueDate:
                if (header === 'Unscheduled') {
                    return { ...request, onlyUnscheduled: true };
                }
                const dueDates = this.externalDueDates(request, header);
                return {
                    ...request,
                    dateTimeChoice: TaskActionViewDateTimeOption.DueDateRange,
                    includeUnscheduled: false,
                    dueDateFrom: dueDates.from,
                    dueDateTo: dueDates.to,
                };
            case TaskActionViewOrganizeBy.LastActivityDate:
                const activity = this.externalLastActivity(request, header);
                return {
                    ...request,
                    useLastActivityFilter: true,
                    lastActivityFrom: activity.from,
                    lastActivityTo: activity.to,
                };
            default:
                return request;
        }
    }

    private withExternalReadiness(request: ActionViewSearchRequest, readiness: unknown): ActionViewSearchRequest {
        const ready =
            readiness === true || readiness === 'ready'
                ? 'ready'
                : readiness === false || readiness === 'not ready'
                  ? 'not ready'
                  : null;
        if (!ready) {
            return request;
        }
        return {
            ...request,
            thatAreChoice: ready === 'ready' ? TaskActionViewThatAreOption.Ready : TaskActionViewThatAreOption.NotReady,
            mostPressingNotReadyOnly: ready === 'not ready',
        };
    }

    private externalDueDates(request: ActionViewSearchRequest, header: string): { from: Date; to: Date } {
        const now = new Date();
        let from: Date;
        let to: Date;
        if (header === 'Overdue') {
            from = moment('1970-01-01').toDate();
            to = this.utcDisplayDate(moment(now).subtract(1, 'days'));
        } else if (header === 'Due Today') {
            from = this.utcDisplayDate(moment(now).startOf('day'));
            to = from;
        } else if (header.includes('+')) {
            from = this.utcDisplayDate(
                moment(now)
                    .add(Number(header.split('+')[0]), 'days')
                    .startOf('day'),
            );
            to = this.utcDisplayDate(moment(now).add(30, 'years').startOf('day'));
        } else {
            const [fromDays, toDays] = header.split(' ')[0].split('-').map(Number);
            from = this.utcDisplayDate(moment(now).add(fromDays, 'days').startOf('day'));
            to = this.utcDisplayDate(moment(now).add(toDays, 'days').startOf('day'));
        }

        if (request.dateTimeChoice === TaskActionViewDateTimeOption.DueWithinDays) {
            const maximum = this.utcDisplayDate(
                moment(now)
                    .add(request.dueWithinDays ?? 0, 'days')
                    .startOf('day'),
            );
            to = new Date(Math.min(to.getTime(), maximum.getTime()));
        } else if (
            request.dateTimeChoice === TaskActionViewDateTimeOption.DueDateRange &&
            request.dueDateFrom &&
            request.dueDateTo
        ) {
            from = new Date(Math.max(from.getTime(), new Date(request.dueDateFrom).getTime()));
            to = new Date(Math.min(to.getTime(), new Date(request.dueDateTo).getTime()));
        }
        return { from, to };
    }

    private externalLastActivity(request: ActionViewSearchRequest, header: string): { from: number; to: number } {
        let from: number;
        let to: number;
        if (header === 'Today') {
            from = 0;
            to = 0;
        } else if (header === '1 Day') {
            from = 1;
            to = 1;
        } else if (header === '2 Days') {
            from = 2;
            to = 2;
        } else if (header === '31+ Days') {
            from = 31;
            to = 9999;
        } else {
            [from, to] = header.split(' ')[0].split('-').map(Number);
        }
        if (request.useLastActivityFilter) {
            from = Math.max(from, request.lastActivityFrom ?? 0);
            to = Math.min(to, request.lastActivityTo ?? 9999);
        }
        return { from, to };
    }

    private utcDisplayDate(value: moment.Moment): Date {
        const local = value.toDate();
        return value.subtract(local.getTimezoneOffset(), 'minutes').toDate();
    }

    private executionCriteria(detailedResults: boolean): ActionViewSearchRequest {
        const request = this.actionViewService.prepareRequest(
            this.criteria,
            this.filterColumns as ActionViewFilterColumn[],
        );
        const selectedGroup = request.groupBy.find((fieldId) => fieldId !== 10001);
        return {
            ...request,
            detailedResults,
            groupBy: [...(request.showMostPressingTasks ? [] : [10001]), ...(selectedGroup ? [selectedGroup] : [])],
        };
    }

    private validate(): boolean {
        if (this.invalidColumns.length) {
            this.toastr.error('Remove incompatible output columns before running the Action View.', 'Error');
            return false;
        }
        if (
            this.criteria.showChoice === TaskActionViewShowOption.SpecificIndividualsTasks &&
            !this.criteria.showUserTeams.some((selection) => selection.userID && selection.teamID)
        ) {
            this.toastr.error('User required for Show', 'Error');
            return false;
        }
        if (
            this.criteria.showChoice === TaskActionViewShowOption.SpecificTeamsTasks &&
            !this.criteria.showTeams.some((selection) => selection.teamID)
        ) {
            this.toastr.error('Team required for Show', 'Error');
            return false;
        }
        if (
            this.criteria.includeChoice === TaskActionViewIncludeOption.CompletedBySpecificIndividual &&
            !this.criteria.includeUserTeams.some((selection) => selection.userID)
        ) {
            this.toastr.error('User required for Include', 'Error');
            return false;
        }
        if (
            this.criteria.dateTimeChoice === TaskActionViewDateTimeOption.DueWithinDays &&
            (!this.criteria.dueWithinDays || this.criteria.dueWithinDays > 365)
        ) {
            this.toastr.error('"Due Within Days" is required and must be no more than 365', 'Error');
            return false;
        }
        if (
            this.criteria.useLastActivityFilter &&
            this.criteria.lastActivityFrom === null &&
            this.criteria.lastActivityTo === null
        ) {
            this.toastr.error('"Last Activity" fields should contain at least one value', 'Error');
            return false;
        }
        if (
            this.criteria.thatAreChoice === TaskActionViewThatAreOption.Completed &&
            this.criteria.completedChoice === TaskActionViewCompletedOption.CompletedWithinLastDays &&
            (!this.criteria.completedWithinDays || this.criteria.completedWithinDays > 365)
        ) {
            this.toastr.error('"Completed Within Days" is required and must be no more than 365', 'Error');
            return false;
        }
        return true;
    }

    private updateInvalidColumns(): void {
        const outputColumns = [...this.criteria.taskOutputColumns, ...this.criteria.entityOutputColumns];
        this.invalidColumns = this.smartSearchService.getInvalidColumns(outputColumns) as InvalidActionViewColumn[];
    }

    private toDisplay(displayType: number, thatAreChoice: TaskActionViewThatAreOption): ActionViewDisplay {
        if (
            displayType === ActionViewDisplayTypes.OVERVIEW &&
            thatAreChoice !== TaskActionViewThatAreOption.Completed
        ) {
            return 'overview';
        }
        if (displayType === ActionViewDisplayTypes.DETAILED) {
            return 'detailed';
        }
        return 'filter';
    }

    private async navigateToSaved(result: ActionViewSaveResult): Promise<void> {
        this.stateService.clear();
        await this.router.stateService.go(
            'actionview',
            {
                actionViewId: result.actionView.actionViewId,
                actionViewType: result.actionViewType,
                displayType: result.actionView.defaultDisplay,
            },
            { reload: true },
        );
    }

    private newCriteria(): ActionViewSearchRequest {
        return normalizeActionViewRequest({
            ShowChoice: TaskActionViewShowOption.MyTasks,
            WhereChoice: TaskActionViewWhereOption.Assigned,
            ThatAreChoice: TaskActionViewThatAreOption.Ready,
            ShowUserTeams: [{}],
            ShowTeams: [{}],
            IncludeUnscheduled: true,
            IncludeUserTeams: [{}],
            IncludeTeams: [{}],
            output: 'default',
            DateTimeChoice: TaskActionViewDateTimeOption.DueWithinDays,
            DueWithinDays: 90,
            CompletedChoice: TaskActionViewCompletedOption.CompletedWithinLastDays,
            CompletedWithinDays: 30,
            IncludeChoice: TaskActionViewIncludeOption.AllCompleted,
            LastActivityTo: null,
            LastActivityFrom: null,
            includeSkipped: true,
            OrganizeBy: TaskActionViewOrganizeBy.Urgency,
            ShowMostPressingTasks: false,
        });
    }

    private emptyDefinition(): ActionViewDefinition {
        return {
            actionViewId: null,
            actionViewName: '',
            actionViewType: ActionViewCategoryTypes.CUSTOM,
            categoryId: null,
            defaultDisplay: ActionViewDisplayTypes.FILTER,
            isFavorite: false,
            isShared: false,
            isOwnedByUser: true,
            rowVersion: [],
        };
    }
}
